import React from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { ToolCardVideo } from '../../../types/chat';
import { useVideoJobCard } from '../../../hooks/useVideoJobCard';
import { describeVideoFailure } from '../../video-generation/videoFailure';
import ToolCardProgress from './ToolCardProgress';
import ToolErrorBody from './ToolErrorBody';
import { VIDEO_MODEL_LABELS } from './toolCardMeta';

interface VideoResultPlayerProps {
  video: ToolCardVideo;
  /** true なら小さめ（一覧用）。リンク類を省く */
  compact?: boolean;
  canRetry?: boolean;
  onToolAction?: (text: string) => void;
  actionsDisabled?: boolean;
  onStatusChange?: (status: string) => void;
  /** 動画広告ページへのリンクを出すか（チャットの動画モードで作った動画は動画広告の一覧に載らないので出さない） */
  showAdsPageLink?: boolean;
}

const aspectClass = (ratio?: string | null): string => {
  if (ratio === '9:16') return 'aspect-[9/16] max-w-[240px]';
  if (ratio === '1:1') return 'aspect-square max-w-[320px]';
  return 'aspect-video max-w-[520px]';
};

/**
 * 動画カードの本体。job_id があればポーリングで完成を追い、完成でインライン再生。
 */
const VideoResultPlayer: React.FC<VideoResultPlayerProps> = ({
  video,
  compact = false,
  canRetry = false,
  onToolAction,
  actionsDisabled = false,
  onStatusChange,
  showAdsPageLink = true,
}) => {
  // 既に url を持つ（履歴・一覧）動画は取得済みなのでポーリングしない。
  const shouldPoll = Boolean(video.job_id) && !video.url;
  const { state, gaveUp } = useVideoJobCard(video.job_id, shouldPoll, {
    status: video.status,
    progress_step: video.progress_step ?? null,
    progress_percentage: video.progress_percentage ?? 0,
    url: video.url ?? null,
    video_model: video.video_model ?? null,
    aspect_ratio: video.aspect_ratio ?? null,
    duration_seconds: video.duration_seconds ?? null,
    resolution: video.resolution ?? null,
    error_code: video.error_code ?? null,
    error_message: video.error_message ?? null,
  });
  const url = shouldPoll ? state.url : video.url ?? null;
  const jobStatus = shouldPoll ? state.status : video.url ? 'completed' : video.status;
  // ポーリングを打ち切った（404 か連続失敗）のに終端が分からない → 状態不明として伝える。
  const fetchFailed = shouldPoll && gaveUp && !url && jobStatus !== 'failed' && jobStatus !== 'cancelled';
  const status = fetchFailed ? 'unknown' : jobStatus;

  React.useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  if (fetchFailed) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-amber-700">
          {showAdsPageLink ? '状態を取得できませんでした。動画広告ページで確認してください。' : '状態を取得できませんでした。しばらくしてからページを開き直してください。'}
        </p>
        {showAdsPageLink && (
          <a href="/ads/video" className="inline-flex items-center gap-1 text-xs text-violet-600 hover:underline">
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            動画広告ページで開く
          </a>
        )}
      </div>
    );
  }

  if (status === 'failed' || status === 'cancelled') {
    return (
      <ToolErrorBody
        message={describeVideoFailure(state.error_code) || state.error_message || '動画生成に失敗しました。'}
        canRetry={canRetry}
        onToolAction={onToolAction}
        disabled={actionsDisabled}
      />
    );
  }

  if (!url) {
    return (
      <ToolCardProgress
        percent={state.progress_percentage}
        step={state.progress_step ?? (status === 'pending' ? 'pending' : null)}
        hint="通常 2〜5 分かかります。ページを離れても生成は続きます。"
      />
    );
  }

  const model = state.video_model ?? video.video_model ?? null;
  const duration = state.duration_seconds ?? video.duration_seconds ?? null;

  return (
    <div>
      <div className={`w-full overflow-hidden rounded-lg bg-black ${aspectClass(state.aspect_ratio ?? video.aspect_ratio)}`}>
        <video src={url} controls preload="metadata" playsInline className="h-full w-full" />
      </div>
      {!compact && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {model && <span className="rounded-md bg-violet-50 px-2 py-0.5 text-violet-700">{VIDEO_MODEL_LABELS[model] ?? model}</span>}
          {duration && <span className="rounded-md bg-gray-100 px-2 py-0.5 text-gray-600">{duration}秒</span>}
          <span className="flex-1" />
          <a href={url} download target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-violet-600 hover:underline">
            <Download className="h-3 w-3" aria-hidden="true" />
            ダウンロード
          </a>
          {showAdsPageLink && (
            <a href="/ads/video" className="inline-flex items-center gap-1 text-violet-600 hover:underline">
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              動画広告ページで開く
            </a>
          )}
        </div>
      )}
      {compact && video.prompt && <p className="mt-1 truncate text-[11px] text-gray-500">{video.prompt}</p>}
    </div>
  );
};

export default VideoResultPlayer;
